import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    RootState,
    useAppDispatch,
    deleteItem,
    ItemDetailsState,
    useGetCartItemsQuery,
    insertItemsToCart,
    emptyItemsInCart,
    useUpdateCartItemMutation,
    useDeleteCartItemMutation,
    useDeleteItemInCartMutation,
    useGetCartCsvFileMutation,
    usePlaceCartOrderMutation,
    updateQuantityByPartNumber,
} from '@circuitry-ai/doc-data';
import { checkType, CtyShowToast } from '../toast/CtyToast';

export enum CartDownloadType {
    CSV = 'csv',
    JSON = 'json',
}

export function useCartOperations() {
    const { itemsList, cartID } = useSelector((state: RootState) => state.globalCartSliceReducer);
    const dispatch = useAppDispatch();
    const token = useSelector((state: RootState) => state.auth.token);

    const [skip, setSkip] = useState<boolean>(true);
    const [placeOrder, setPlaceOrder] = useState<boolean>(false);

    const [updateCartItem] = useUpdateCartItemMutation();
    const [deleteCartItem] = useDeleteCartItemMutation();
    const [deleteItemInCart] = useDeleteItemInCartMutation();

    const { data: cartItemsList, refetch } = useGetCartItemsQuery(undefined, {
        refetchOnMountOrArgChange: true,
        skip,
    });
    const [getCartCsvFile, { isLoading: isFetchingCartCsvFile }] = useGetCartCsvFileMutation();
    const [placeCartOrder, { isLoading: isPlacingOrder }] = usePlaceCartOrderMutation();

    useEffect(() => {
        setSkip(!token);
    }, [token]);

    useEffect(() => {
        if (cartItemsList?.[0]?.items?.length > 0) {
            const mapped = cartItemsList[0].items.reduce((acc: any, item: any) => {
                acc[item.code] = {
                    totalQuanity: item.quantity,
                    inCart: true,
                    id: item.id,
                    partNumber: item.code,
                    partName: item.name,
                };
                return acc;
            }, {});
            dispatch(insertItemsToCart({ itemsList: mapped, cartID: cartItemsList[0].cart_id }));
        }
    }, [cartItemsList, dispatch]);

    const incrementQuantity = async (item: ItemDetailsState) => {
        try {
            dispatch(updateQuantityByPartNumber({ partNumber: item.partNumber, type: 'increase' }));
            await updateCartItem({
                cart_id: cartID,
                name: item.partName,
                code: item.partNumber,
                type: 'part',
                price: 0,
            });
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message || 'Operation failed');
        }
    };

    const decrementQuantity = async (item: ItemDetailsState) => {
        try {
            dispatch(updateQuantityByPartNumber({ partNumber: item.partNumber, type: 'decrease' }));
            await deleteCartItem({ code: item.partNumber, cartID });
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message || 'Operation failed');
        }
    };

    const handleDelete = async (item: ItemDetailsState) => {
        try {
            dispatch(deleteItem({ partNumber: item.partNumber }));
            await deleteItemInCart({ itemID: item.id });
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message || 'Operation failed');
        }
    };

    const handleDownload = async (type: CartDownloadType) => {
        try {
            if (type === CartDownloadType.CSV) {
                const response = await getCartCsvFile(cartID);
                window.open(response.data, '_blank');
                dispatch(emptyItemsInCart());
            } else {
                const response = await placeCartOrder(cartID);
                const jsonStr = JSON.stringify(response, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'data.json';
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message || 'Operation failed');
        } finally {
            setPlaceOrder(false);
            refetch();
        }
    };

    const itemsArray = useMemo(
        () => Object.entries(itemsList) as Array<[string, ItemDetailsState]>,
        [itemsList]
    );

    const itemCount = itemsArray.length;

    return {
        itemsList,
        cartID,
        itemsArray,
        itemCount,
        placeOrder,
        setPlaceOrder,
        isFetchingCartCsvFile,
        isPlacingOrder,
        incrementQuantity,
        decrementQuantity,
        handleDelete,
        handleDownload,
    };
}
